import { useCallback, useRef } from 'react';
import { idempotencyKey } from './lib';

/**
 * S-27 — an idempotency key that belongs to a SUBMISSION, not to a dialog.
 *
 * §34 says every financial mutation carries a key so a replay is a no-op. The
 * pattern the payment dialogs use — `useState(() => idempotencyKey(...))` —
 * mints one key when the component mounts, which is right there because the
 * dialog closes the moment the payment lands: one mount, one payment.
 *
 * The drawer's movement form is the opposite. It stays mounted and is used over
 * and over: a cashier tops the float up with 50.00 at 10:02 and again at 10:41.
 * A per-mount key would make the second one a replay of the first — the server
 * would answer 200 with the drawer as it already stood, the toast would say
 * "recorded", and 50.00 of real cash would be missing at the count with no
 * error anywhere to explain it.
 *
 * So the key is minted per submission and identified by what is being
 * submitted:
 *
 *   - the SAME content, sent again after a failure or a timeout → same key,
 *     which is exactly the retry the server is meant to collapse;
 *   - a DIFFERENT amount, reason, direction or drawer → new key, because it is
 *     a different movement even if it looks like the last one;
 *   - a submission that succeeded → `rotate()`, so the next one is never a
 *     replay of it, however identical it looks.
 *
 * The signature matters as much as the rotation: keeping one key across an
 * edited amount would let a corrected 500.00 be swallowed as a replay of the
 * 50.00 that failed.
 */
export interface SubmissionKey {
  /** The key for this submission. Stable while `signature` is, so a retry replays. */
  keyFor: (signature: string) => string;
  /** Call after the server accepted it: the next submission is a new mutation. */
  rotate: () => void;
}

export function useSubmissionKey(prefix: string): SubmissionKey {
  const held = useRef<{ signature: string; key: string } | null>(null);

  const keyFor = useCallback(
    (signature: string) => {
      if (!held.current || held.current.signature !== signature) {
        held.current = { signature, key: idempotencyKey(prefix) };
      }
      return held.current.key;
    },
    [prefix],
  );

  const rotate = useCallback(() => {
    held.current = null;
  }, []);

  return { keyFor, rotate };
}
